import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ExternalServiceProvidersService } from '../../../services/external-service-providers.service';
import { ExternalServiceProvider } from '../../../models/ExternalServiceProvider';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2B_EXPORT_PAGE_SIZE, exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';
import { getServiceTypeCssClass } from '../../../models/ExternalServiceProviderEnums';

@Component({
  selector: 'app-external-service-providers',
  templateUrl: './external-service-providers.component.html',
  styleUrls: ['./external-service-providers.component.css']
})
export class ExternalServiceProvidersComponent implements OnInit {
  providers: ExternalServiceProvider[] = [];
  isLoading = false;

  // Paginação (backend)
  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  searchTerm = '';
  sortBy = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  columns = [
    { titulo: 'Nome ', label: 'tradeName' },
    { titulo: 'CNPJ', label: 'cnpj', formatter: (value: any) => this.formatCnpj(value) },
    { titulo: 'Telefone', label: 'phone', formatter: (value: any) => this.formatPhone(value) },
    {
      titulo: 'Tipo de Serviço',
      label: 'serviceType',
      customClass: (value: any) => this.serviceTypeCellClass(value)
    },
    { titulo: 'Estados Atendidos (UF)', label: 'servedStates', formatter: (value: any) => this.formatServedStates(value) }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private router: Router,
    private externalServiceProvidersService: ExternalServiceProvidersService
  ) {}

  ngOnInit(): void {
    this.loadProviders();
  }

  loadProviders(): void {
    this.isLoading = true;
    this.externalServiceProvidersService
      .list(this.pageNumber, this.pageSize, this.searchTerm)
      .subscribe({
        next: res => {
          this.providers = res.items;
          this.totalItems = res.pagination?.totalCount || res.items.length;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.pageNumber = 1;
    this.loadProviders();
  }

  onPageChange(page: number): void {
    this.pageNumber = page;
    this.loadProviders();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageNumber = 1;
    this.loadProviders();
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.sortBy = event.sortBy;
    this.sortOrder = event.sortOrder;
    this.providers = [...this.providers].sort((a, b) => {
      const va = (a as any)[event.sortBy] ?? '';
      const vb = (b as any)[event.sortBy] ?? '';
      const cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  exportExcel(): void {
    const size = Math.max(this.totalItems || 0, B2B_EXPORT_PAGE_SIZE);
    this.externalServiceProvidersService.list(1, size, this.searchTerm).subscribe({
      next: res => {
        exportB2bTableToExcel('b2b-prestadores', this.columns, res.items || [], 'Prestadores');
      }
    });
  }

  newProvider(): void {
    this.router.navigate(['/b2b/external-service-providers/new']);
  }

  view(provider: ExternalServiceProvider): void {
    this.router.navigate(['/b2b/external-service-providers', provider.id, 'view']);
  }

  edit(provider: ExternalServiceProvider): void {
    this.router.navigate(['/b2b/external-service-providers', provider.id, 'edit']);
  }

  remove(id: number): void {
    this.externalServiceProvidersService.delete(id).subscribe(() => this.loadProviders());
  }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;
    const provider = item as ExternalServiceProvider;

    switch (action) {
      case 'view':
        this.view(provider);
        break;
      case 'edit':
        this.edit(provider);
        break;
      case 'delete':
        if (confirm(`Tem certeza que deseja excluir o prestador "${provider?.tradeName || provider?.companyName || provider?.id}"?`)) {
          this.remove(provider.id);
        }
        break;
      default:
        // eslint-disable-next-line no-console
    }
  }

  /**
   * Formata CNPJ: 00.000.000/0000-00
   */
  formatCnpj(value: any): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length !== 14) return value;
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  /**
   * Formata telefone: (00) 0000-0000 ou (00) 00000-0000
   */
  formatPhone(value: any): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 10) {
      return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    } else if (digits.length === 11) {
      return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    return value;
  }

  formatServedStates(value: any): string {
    if (!value) return '— (sem UF)';
    const text = String(value).trim();
    return text || '— (sem UF)';
  }

  serviceTypeCellClass(value: any): string {
    return getServiceTypeCssClass(value ? String(value) : null);
  }
}


